import { queryOptions, useQuery } from '@tanstack/react-query';
import { apiClient, type ApiResponse } from '@/api';
import { TENANT_API_URLS } from '@/api/constants/api.url';
import type { TenantInfo } from '@/shared/types/session';
import type { TenantSubtype } from '@propertyscan/shared/types';
import { useIsPlatformAdmin } from '@/shared/hooks/use-is-platform-admin';

/**
 * Tenant metadata. Lives in the dashboard module because (a) it isn't
 * scoped to any one feature, (b) the layout + half the feature modules
 * read it. Module placement was chosen so cross-feature consumers don't
 * have to dig through `auth/` or `properties/` to find it.
 *
 * Direct port of legacy `getTenant()` from apps/dashboard/src/lib/tenant.server.ts.
 * The legacy fn used React `cache()` for request-dedup; TanStack Query
 * provides equivalent dedup automatically via the query key.
 */
export const tenantQueryKeys = {
    all: ['tenant'] as const,
    me:  () => [...tenantQueryKeys.all, 'me'] as const,
} as const;

const tenantQueryOptions = {
    me: () => queryOptions({
        queryKey: tenantQueryKeys.me(),
        queryFn:  async (): Promise<TenantInfo | null> => {
            try {
                const { data } = await apiClient.get<ApiResponse<{ tenant: TenantInfo }>>(TENANT_API_URLS.me);
                return data.data.tenant;
            } catch {
                // Match legacy fallback: return null on API hiccup, callers
                // fall back to permissive defaults.
                return null;
            }
        },
        // Tenant metadata barely changes; cache aggressively.
        staleTime: 5 * 60_000,
    }),
};

export function useTenantQuery() {
    const isPlatformAdmin = useIsPlatformAdmin();
    return useQuery({
        ...tenantQueryOptions.me(),
        // Dead weight for the platform admin — see useIsPlatformAdmin.
        enabled: !isPlatformAdmin,
    });
}

/** Quick boolean — convenience for component-level if-branches. */
export function isAgentSubtype(s: TenantSubtype | null | undefined): boolean {
    return s === 'solo_agent' || s === 'team';
}
